import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { ImageNotSupported as ImageNotSupportedIcon } from "@material-ui/icons";
import type { ProcessedListingData } from "../../../@types/sharedSchema";
import { fetchListingById } from "../../../services/signaturesListings";

/**
 * "street houseNumber, city" — used as the chip label. Shared with the
 * sent-list cards and the detail panel so the listing text stays identical
 * across surfaces.
 */
export const formatShortAddress = (listing: ProcessedListingData | null): string => {
  const a = listing?.address;
  if (!a) return "";
  const street = [a.street, a.houseNumber]
    .filter((v) => v !== undefined && v !== "")
    .join(" ");
  return [street, a.city].filter(Boolean).join(", ");
};

type ListingChipProps = {
  listingId: string;
  onClick: (listingId: string) => void;
};

/**
 * Small lazy-loaded thumbnail + address chip for a listing referenced by a
 * signature document. Shown inline on the sent-list cards and inside the
 * detail panel so the realtor can see which property each document relates
 * to at a glance. Fetching is self-contained (per-chip) so callers don't
 * need to prefetch listings by id.
 */
export default function ListingChip({ listingId, onClick }: ListingChipProps) {
  const { t } = useTranslation();
  const [listing, setListing] = useState<ProcessedListingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchListingById(listingId)
      .then((l) => {
        if (!cancelled) setListing(l);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [listingId]);

  const imageUrl = listing?.coverImageUrl || listing?.imageUrls?.[0];
  const label =
    formatShortAddress(listing) ||
    (loading
      ? t("signatures.card.loadingPlaceholder")
      : t("signatures.card.listingPlaceholder"));

  return (
    <Box
      onClick={(e) => {
        e.stopPropagation();
        onClick(listingId);
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        py: 0.5,
        pr: 1,
        pl: 0.5,
        bgcolor: "grey.100",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        cursor: "pointer",
        maxWidth: "100%",
        minWidth: 0,
        "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" }
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.300",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0
        }}
      >
        {imageUrl ? (
          // eslint-disable-next-line jsx-a11y/alt-text
          <img
            src={imageUrl}
            alt={label}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : loading ? (
          <CircularProgress size={14} />
        ) : (
          <ImageNotSupportedIcon fontSize="small" sx={{ color: "grey.500" }} />
        )}
      </Box>
      <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
        {label}
      </Typography>
    </Box>
  );
}
